import {
  InventoryItemJobProgressionBillingInfo,
  InventoryItemJobProgressionEssential,
  InventoryItemJobProgressionOutsourcing,
  InventoryItemJobProgressionPreparation,
  InventoryItemJobProgressionRetail,
  InventoryItemJobProgressionReview,
  type InventoryItemForProviders as InventoryItemForProvidersProto,
  InventoryItemPriceType as InventoryItemPriceTypeProto,
  InventoryItemSupportingItem as InventoryItemSupportingItemProto,
  InventoryItemProVisibility as InventoryItemProVisibilityProto,
  InventoryItemTypeSpecification as InventoryItemTypeSpecificationProto,
  ProgressionStepBillingValues,
} from '@taxfyle/api-internal/internal/inventory_item_pb'
import {
  safeDecimalToNumber,
  decimalToNumber,
  numberToDecimal,
} from '@/utils/grpc-util'
import { BadRequest } from 'fejl'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { type InventoryItemForProvider } from '@/features/inventory/InventoryItemForProvider'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import { type InventoryItemPricingValues } from '@/features/inventory/inventory-item-pricing-values'
import type { UpdateInventoryItemParams } from '@/features/inventory/InventoryStore'
import {
  type InventoryItemSupportingItem,
  InventoryItemBookkeepingTypeSpecification,
  InventoryItemConsultationTypeSpecification,
  InventoryItemDefaultTypeSpecification,
  type InventoryItemExtensionBillingType,
  type InventoryItemExtensionType,
  InventoryItemExtensionTypeSpecification,
  type InventoryItemPricing,
  type InventoryItemProps,
  type InventoryItemProVisibility,
  InventoryItemTaxTypeSpecification,
  type InventoryItemTypeSpecification,
} from '@/features/inventory/InventoryItem'
import {
  type AllowedProgressionSteps,
  type AllowedProgressionTypes,
} from '@/features/inventory/allowed-progressions'
import {
  type ExtensionBillingInfo,
  type ExtensionProgressionBilling,
  type ExtensionRegularBilling,
} from '@/features/inventory/ExtensionBillingInfo'

export function mapExtensionTypeToProto(
  type: InventoryItemExtensionType,
): InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionType {
  switch (type) {
    case 'Personal':
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionType.PERSONAL
    case 'Business':
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionType.BUSINESS
    case 'Outsourcing':
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionType.OUTSOURCING
  }
}

export function mapExtensionBillingTypeToProto(
  type: InventoryItemExtensionBillingType,
): InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionBillingType {
  switch (type) {
    case 'Percentage':
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionBillingType.PERCENTAGE
    case 'FlatFee':
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionBillingType.FLAT_FEE
  }
}

export function mapExtensionBillingInfoToProto(
  billingInfo: ExtensionBillingInfo,
): InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo {
  switch (billingInfo.type) {
    case 'Regular':
      return new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo().setRegular(
        new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.RegularBillingInfo()
          .setClientChargeAmount(
            numberToDecimal(billingInfo.clientChargeAmount),
          )
          .setProPayoutAmount(numberToDecimal(billingInfo.proPayoutAmount)),
      )
    case 'Progression':
      return new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo().setJobProgression(
        mapExtensionProgressionBillingToProto(billingInfo),
      )
  }
}

function mapExtensionProgressionBillingToProto(
  billingInfo: ExtensionProgressionBilling,
): InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.JobProgressionBillingInfo {
  const billing = billingInfo.progressions.reduce((billing, progression) => {
    switch (progression.progression) {
      case 'Outsourcing':
        billing.setOutsourcing(
          progression.steps.reduce((outsourcing, step) => {
            switch (step.step) {
              case 'OpenItems':
                outsourcing.setOpenItems(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'JobStarted':
                outsourcing.setJobStarted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftRejected':
                outsourcing.setDraftRejected(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftInReview':
                outsourcing.setDraftInReview(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftApproved':
                outsourcing.setDraftApproved(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return outsourcing
          }, new InventoryItemJobProgressionOutsourcing()),
        )
        break
      case 'Review':
        billing.setReview(
          progression.steps.reduce((review, step) => {
            switch (step.step) {
              case 'OpenItems':
                review.setOpenItems(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'JobStarted':
                review.setJobStarted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftRejected':
                review.setDraftRejected(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftInReview':
                review.setDraftInReview(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'ReviewCompleted':
                review.setReviewCompleted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftApproved':
                review.setDraftApproved(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return review
          }, new InventoryItemJobProgressionReview()),
        )
        break
      case 'Retail':
        billing.setRetail(
          progression.steps.reduce((retail, step) => {
            switch (step.step) {
              case 'OpenItems':
                retail.setOpenItems(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'JobStarted':
                retail.setJobStarted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftRejected':
                retail.setDraftRejected(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftInReview':
                retail.setDraftInReview(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DraftApproved':
                retail.setDraftApproved(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'AuthorizationSigned':
                retail.setAuthorizationSigned(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'ReturnFiled':
                retail.setReturnFiled(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return retail
          }, new InventoryItemJobProgressionRetail()),
        )
        break
      case 'Preparation':
        billing.setPreparation(
          progression.steps.reduce((preparation, step) => {
            switch (step.step) {
              case 'OpenItems':
                preparation.setOpenItems(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'JobStarted':
                preparation.setJobStarted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'PreparationCompleted':
                preparation.setPreparationCompleted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return preparation
          }, new InventoryItemJobProgressionPreparation()),
        )
        break
      case 'Essential':
        billing.setEssential(
          progression.steps.reduce((essential, step) => {
            switch (step.step) {
              case 'JobStarted':
                essential.setJobStarted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case 'DeliverableCompleted':
                essential.setDeliverableCompleted(
                  mapExtensionProgressionStepAmountsToProto(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return essential
          }, new InventoryItemJobProgressionEssential()),
        )
        break
    }

    return billing
  }, new InventoryItemJobProgressionBillingInfo())

  return new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.JobProgressionBillingInfo().setBillingInfo(
    billing,
  )
}

function mapExtensionProgressionStepAmountsToProto(
  clientChargeAmount: number,
  proPayoutAmount: number,
): ProgressionStepBillingValues {
  return new ProgressionStepBillingValues()
    .setClientChargeAmount(numberToDecimal(clientChargeAmount))
    .setProPayoutAmount(numberToDecimal(proPayoutAmount))
}

export function mapInventoryItemTypeSpecificationProtoToProps(
  spec: InventoryItemTypeSpecificationProto,
): InventoryItemTypeSpecification {
  const options = spec.getSpec()
  if (!options) {
    throw new BadRequest('Type specification not found')
  }

  switch (options.getTypeCase()) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.DEFAULT:
      return new InventoryItemDefaultTypeSpecification()
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.BOOKKEEPING: {
      const bookkeeping = options.getBookkeeping()
      if (!bookkeeping) {
        throw new BadRequest('Bookkeeping type specification not found')
      }
      return mapBookkeepingTypeSpecificationProtoToProps(bookkeeping)
    }
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase
      .CONSULTATION: {
      const consultation = options.getConsultation()
      if (!consultation) {
        throw new BadRequest('Consultation type specification not found')
      }
      return mapConsultationTypeSpecificationProtoToProps(consultation)
    }
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.TAX: {
      const tax = options.getTax()
      if (!tax) {
        throw new BadRequest('Tax type specification not found')
      }
      return mapTaxTypeSpecificationProtoToProps(tax)
    }
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase
      .TAX_EXTENSION: {
      const taxExtension = options.getTaxExtension()
      if (!taxExtension) {
        throw new BadRequest('Extension type specification not found')
      }
      return mapExtensionTypeSpecificationProtoToProps(taxExtension)
    }
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.TYPE_NOT_SET:
      throw new BadRequest('Unsupported type specification')
  }
}

function mapConsultationTypeSpecificationProtoToProps(
  spec: InventoryItemTypeSpecificationProto.TypeOptions.Consultation,
): InventoryItemConsultationTypeSpecification {
  return new InventoryItemConsultationTypeSpecification(
    spec.getLengthInMinutes(),
  )
}

function mapBookkeepingTypeSpecificationProtoToProps(
  spec: InventoryItemTypeSpecificationProto.TypeOptions.Bookkeeping,
): InventoryItemBookkeepingTypeSpecification {
  return new InventoryItemBookkeepingTypeSpecification(spec.getRecurring())
}

function mapTaxTypeSpecificationProtoToProps(
  spec: InventoryItemTypeSpecificationProto.TypeOptions.Tax,
): InventoryItemTaxTypeSpecification {
  const reviewPrice = spec.getReviewPrice()
  const reviewPayout = spec.getReviewProPrice()

  if (reviewPrice === undefined || reviewPayout === undefined) {
    throw new BadRequest('Tax type specification not found')
  }

  return new InventoryItemTaxTypeSpecification(
    decimalToNumber(reviewPrice),
    decimalToNumber(reviewPayout),
  )
}

function mapExtensionTypeSpecificationProtoToProps(
  spec: InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension,
): InventoryItemExtensionTypeSpecification {
  const billingInfo = spec.getBillingInfo()

  if (billingInfo === undefined) {
    throw new BadRequest('Extension type specification type error')
  }

  return new InventoryItemExtensionTypeSpecification(
    mapExtensionTypeProtoToExtensionType(spec.getType()),
    mapExtensionBillingTypeProtoToExtensionBillingType(spec.getBillingType()),
    mapExtensionBillingInfoProtoToExtensionBillingInfo(billingInfo),
  )
}

function mapExtensionTypeProtoToExtensionType(
  type: InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionType,
): InventoryItemExtensionType {
  switch (type) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionType.PERSONAL:
      return 'Personal'
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionType.BUSINESS:
      return 'Business'
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionType.OUTSOURCING:
      return 'Outsourcing'
  }
}

function mapExtensionBillingTypeProtoToExtensionBillingType(
  type: InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionBillingType,
): InventoryItemExtensionBillingType {
  switch (type) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionBillingType.FLAT_FEE:
      return 'FlatFee'
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionBillingType.PERCENTAGE:
      return 'Percentage'
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionBillingType.EXTENSION_BILLING_TYPE_UNSPECIFIED:
      return 'FlatFee'
  }
}

function mapExtensionBillingInfoProtoToExtensionBillingInfo(
  billingInfo: InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo,
): ExtensionBillingInfo {
  switch (billingInfo.getTypeCase()) {
    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .REGULAR:
    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .UNSPECIFIED:
      return mapRegularBillingInfoProtoToRegularBilling(
        billingInfo.getRegular(),
      )
    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .JOB_PROGRESSION:
      return mapProgressionBillingInfoProtoToProgressionBilling(
        billingInfo.getJobProgression(),
      )
    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .TYPE_NOT_SET:
      throw new BadRequest('Unsupported extension billing info')
  }
}

// An unset regular billing reads as zero amounts.
function mapRegularBillingInfoProtoToRegularBilling(
  regularBilling:
    | InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.RegularBillingInfo
    | undefined,
): ExtensionRegularBilling {
  return {
    type: 'Regular',
    clientChargeAmount: safeDecimalToNumber(
      regularBilling?.getClientChargeAmount(),
    ),
    proPayoutAmount: safeDecimalToNumber(regularBilling?.getProPayoutAmount()),
  }
}

type ProgressionProtoKey = keyof InventoryItemJobProgressionBillingInfo.AsObject

type ProgressionStepProtoKey =
  | keyof InventoryItemJobProgressionOutsourcing.AsObject
  | keyof InventoryItemJobProgressionRetail.AsObject
  | keyof InventoryItemJobProgressionEssential.AsObject
  | keyof InventoryItemJobProgressionPreparation.AsObject
  | keyof InventoryItemJobProgressionReview.AsObject

// An unset progression billing reads as no progressions.
function mapProgressionBillingInfoProtoToProgressionBilling(
  progressionBilling:
    | InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.JobProgressionBillingInfo
    | undefined,
): ExtensionProgressionBilling {
  const progressionBillingProto = progressionBilling
    ?.getBillingInfo()
    ?.toObject()

  const progressions = []

  if (progressionBillingProto) {
    // `Object.entries` widens the keys to `string`; they are the proto's
    // field names.
    for (const [progressionKey, progressionValue] of Object.entries(
      progressionBillingProto,
    ) as [ProgressionProtoKey, Record<string, unknown>][]) {
      const steps = []

      for (const [stepKey, stepValue] of Object.entries(progressionValue) as [
        ProgressionStepProtoKey,
        ProgressionStepBillingValues.AsObject,
      ][]) {
        const clientChargeAmount = safeDecimalToNumber(
          stepValue.clientChargeAmount,
        )

        const proPayoutAmount = safeDecimalToNumber(stepValue.proPayoutAmount)

        if (clientChargeAmount || proPayoutAmount) {
          steps.push({
            step: mapProgressionStepProtoKeyToProgressionStep(stepKey),
            clientChargeAmount,
            proPayoutAmount,
          })
        }
      }

      if (steps.length) {
        progressions.push({
          progression: mapProgressionProtoKeyToProgressionType(progressionKey),
          steps,
        })
      }
    }
  }

  return {
    type: 'Progression',
    progressions,
  }
}

function mapProgressionProtoKeyToProgressionType(
  progression: ProgressionProtoKey,
): AllowedProgressionTypes {
  switch (progression) {
    case 'outsourcing':
      return 'Outsourcing'
    case 'essential':
      return 'Essential'
    case 'retail':
      return 'Retail'
    case 'preparation':
      return 'Preparation'
    case 'review':
      return 'Review'
  }
}

function mapProgressionStepProtoKeyToProgressionStep(
  step: ProgressionStepProtoKey,
): AllowedProgressionSteps {
  switch (step) {
    case 'jobStarted':
      return 'JobStarted'
    case 'openItems':
      return 'OpenItems'
    case 'draftInReview':
      return 'DraftInReview'
    case 'draftRejected':
      return 'DraftRejected'
    case 'draftApproved':
      return 'DraftApproved'
    case 'authorizationSigned':
      return 'AuthorizationSigned'
    case 'reviewCompleted':
      return 'ReviewCompleted'
    case 'preparationCompleted':
      return 'PreparationCompleted'
    case 'returnFiled':
      return 'ReturnFiled'
    case 'deliverableCompleted':
      return 'DeliverableCompleted'
  }
}

export function mapInventoryItemPriceTypeProtoToPricing(
  priceType: InventoryItemPriceTypeProto,
): InventoryItemPricing {
  switch (priceType) {
    case InventoryItemPriceTypeProto.FEE:
      return 'Fee'
    case InventoryItemPriceTypeProto.FLAT:
      return 'Flat'
    case InventoryItemPriceTypeProto.MARKUP:
      return 'Markup'
    case InventoryItemPriceTypeProto.MARKDOWN:
      return 'Markdown'
  }
}

export function mapInventoryItemProVisibilityToProto(
  visibility: InventoryItemProVisibility,
): InventoryItemProVisibilityProto {
  switch (visibility) {
    case 'Hidden':
      return InventoryItemProVisibilityProto.HIDDEN
    case 'Visible':
      return InventoryItemProVisibilityProto.VISIBLE
  }
}

export function mapInventoryItemForProviderProtoToProps(
  proto: InventoryItemForProvidersProto,
): InventoryItemForProvider {
  return {
    id: proto.getId(),
    rootId: proto.getRootId(),
    workspaceId: proto.getWorkspaceId(),
    name: proto.getName(),
    description: proto.getDescription(),
    shortId: proto.getShortId(),
    versionId: proto.getVersionId(),
  }
}

export function mapInventoryItemProVisibilityProtoToProVisibility(
  visibility: InventoryItemProVisibilityProto,
): InventoryItemProVisibility {
  switch (visibility) {
    case InventoryItemProVisibilityProto.HIDDEN:
      return 'Hidden'
    case InventoryItemProVisibilityProto.VISIBLE:
      return 'Visible'
  }
}

/**
 * The API represents rate prices as decimals; the model holds percentages.
 */
export function mapInventoryItemPriceProtoToPrice(
  price: number,
  pricing: InventoryItemPricing,
) {
  return pricing === 'Markup' || pricing === 'Markdown' ? price * 100 : price
}

export function mapSupportingItemsToProto(
  items: InventoryItemSupportingItem[],
): InventoryItemSupportingItemProto[] {
  return items.map((item) => {
    const supportingItem = new InventoryItemSupportingItemProto()
    const spec = new InventoryItemSupportingItemProto.Spec()

    switch (item.spec.type) {
      case 'Document':
        spec.setDocument(
          new InventoryItemSupportingItemProto.Spec.Document().setTypeShortId(
            Id.assert(item.spec.documentType.id),
          ),
        )
        break
      case 'BusinessInfo':
        spec.setBusinessInfo(
          new InventoryItemSupportingItemProto.Spec.BusinessInfo(),
        )
        break
      case 'BusinessRefundMethodInfo':
        spec.setBusinessRefundMethod(
          new InventoryItemSupportingItemProto.Spec.BusinessRefundMethod(),
        )
        break
      case 'DependentsInfo':
        spec.setDependentsInfo(
          new InventoryItemSupportingItemProto.Spec.DependentsInfo(),
        )
        break
      case 'Notes':
        spec.setNotes(new InventoryItemSupportingItemProto.Spec.Notes())
        break
      case 'PersonalInfo':
        spec.setPersonalInfo(
          new InventoryItemSupportingItemProto.Spec.PersonalInfo(),
        )
        break
      case 'PersonalRefundMethodInfo':
        spec.setPersonalRefundMethod(
          new InventoryItemSupportingItemProto.Spec.PersonalRefundMethod(),
        )
        break
      case 'SpousalInfo':
        spec.setSpousalInfo(
          new InventoryItemSupportingItemProto.Spec.SpousalInfo(),
        )
        break
    }

    supportingItem.setSpec(spec)
    supportingItem.setRequired(item.required)
    return supportingItem
  })
}

export function mapInventoryItemTypeSpecificationToProto(
  spec: InventoryItemTypeSpecification,
): InventoryItemTypeSpecificationProto {
  return match(spec.type, {
    Default: () =>
      new InventoryItemTypeSpecificationProto().setSpec(
        new InventoryItemTypeSpecificationProto.TypeOptions().setDefault(
          new Empty(),
        ),
      ),
    Bookkeeping: () =>
      new InventoryItemTypeSpecificationProto().setSpec(
        new InventoryItemTypeSpecificationProto.TypeOptions().setBookkeeping(
          new InventoryItemTypeSpecificationProto.TypeOptions.Bookkeeping().setRecurring(
            (spec as InventoryItemBookkeepingTypeSpecification).recurring,
          ),
        ),
      ),
    Consultation: () =>
      new InventoryItemTypeSpecificationProto().setSpec(
        new InventoryItemTypeSpecificationProto.TypeOptions().setConsultation(
          new InventoryItemTypeSpecificationProto.TypeOptions.Consultation().setLengthInMinutes(
            (spec as InventoryItemConsultationTypeSpecification)
              .lengthInMinutes,
          ),
        ),
      ),
    Tax: () =>
      new InventoryItemTypeSpecificationProto().setSpec(
        new InventoryItemTypeSpecificationProto.TypeOptions().setTax(
          new InventoryItemTypeSpecificationProto.TypeOptions.Tax()
            .setReviewPrice(
              numberToDecimal(
                (spec as InventoryItemTaxTypeSpecification).reviewPrice,
              ),
            )
            .setReviewProPrice(
              numberToDecimal(
                (spec as InventoryItemTaxTypeSpecification).reviewPayout,
              ),
            ),
        ),
      ),
    Extension: () => {
      const extensionSpec = spec as InventoryItemExtensionTypeSpecification

      const taxExtension =
        new InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension()

      taxExtension
        .setType(mapExtensionTypeToProto(extensionSpec.extensionType))
        .setBillingType(
          mapExtensionBillingTypeToProto(extensionSpec.billingType),
        )
        .setBillingInfo(
          mapExtensionBillingInfoToProto(extensionSpec.billingInfo),
        )

      return new InventoryItemTypeSpecificationProto().setSpec(
        new InventoryItemTypeSpecificationProto.TypeOptions().setTaxExtension(
          taxExtension,
        ),
      )
    },
  })
}

/**
 * Maps from the Editor Values representation to a decimal.
 */
export function mapPricingValuesToClientPrice(
  pricing: InventoryItemPricingValues,
) {
  switch (pricing.type) {
    case 'Fee':
      return pricing.fee
    case 'Flat':
      return pricing.clientPrice
    case 'Markup':
      return pricing.clientMarkupRate / 100
    case 'Markdown':
      return pricing.clientMarkdownRate / 100
  }
}

/**
 * Maps from the Editor Values representation to a decimal.
 */
export function mapPricingValuesToProPayout(
  pricing: InventoryItemPricingValues,
) {
  switch (pricing.type) {
    case 'Fee':
      return 0
    case 'Flat':
      return pricing.proPayoutAmount
    case 'Markup':
      return pricing.proPayoutMarkupRate / 100
    case 'Markdown':
      return pricing.proPayoutMarkdownRate / 100
  }
}

/**
 * Maps from the Editor Values representation to the proto enum.
 */
export function mapPricingValuesToPriceTypeProto(
  pricing: InventoryItemPricingValues,
) {
  return match(pricing.type, {
    Fee: () => InventoryItemPriceTypeProto.FEE,
    Flat: () => InventoryItemPriceTypeProto.FLAT,
    Markdown: () => InventoryItemPriceTypeProto.MARKDOWN,
    Markup: () => InventoryItemPriceTypeProto.MARKUP,
  })
}

export function mapUpdateInventoryItemParamsToProps(
  params: UpdateInventoryItemParams,
  versionId: string,
): Partial<InventoryItemProps> {
  return {
    versionId,
    name: params.name,
    description: params.description,
    courtesyAmount: params.courtesyAmount,
    supportingItems: params.supportingItems,
    ...(params.typeSpecification !== null && {
      typeSpecification: params.typeSpecification,
    }),
  }
}
